
import React, { useState, useEffect } from 'react';
import { RESOURCES } from '../constants';
import { Resource, Page } from '../types';

interface ResourcesProps {
  setPage: (page: Page) => void;
}

const Resources: React.FC<ResourcesProps> = ({ setPage }) => {
  const [filter, setFilter] = useState('All');
  const [selectedResource, setSelectedResource] = useState<Resource | null>(null);
  
  const categories = ['All', 'Tax Law', 'AI & Tech', 'CAC', 'Startup'];

  const filteredResources = filter === 'All' 
    ? RESOURCES 
    : RESOURCES.filter(r => r.category === filter);

  // Smooth scroll to top when opening an article
  useEffect(() => {
    if (selectedResource) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [selectedResource]);

  if (selectedResource) {
    return (
      <div className="pt-32 pb-24 bg-white dark:bg-slate-950 min-h-screen animate-reveal">
        <div className="max-w-4xl mx-auto px-4">
          <button 
            onClick={() => setSelectedResource(null)}
            className="group mb-12 flex items-center gap-2 text-slate-400 hover:text-primary transition-all font-bold text-sm"
          >
            <span className="material-symbols-rounded group-hover:-translate-x-1 transition-transform">arrow_back</span>
            Back to Knowledge Hub
          </button>

          <header className="mb-16">
            <div className="flex items-center gap-4 mb-8">
              <span className="px-4 py-1.5 rounded-full bg-primary/5 dark:bg-white/5 text-primary dark:text-accent-400 text-[10px] font-black uppercase tracking-widest border border-primary/10">
                {selectedResource.category}
              </span>
              <span className="text-slate-400 text-xs font-bold">{selectedResource.date}</span>
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-display font-bold text-slate-900 dark:text-white leading-[1.1] mb-8">
              {selectedResource.title}
            </h1>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-slate-100 dark:bg-slate-800 flex items-center justify-center">
                <span className="material-symbols-rounded text-primary">person</span>
              </div>
              <div>
                <p className="text-sm font-bold text-slate-900 dark:text-white">FinTrack Editorial Team</p>
                <p className="text-xs text-slate-500">Regulatory Analysis Desk</p>
              </div>
            </div>
          </header>

          {/* Article Content with Typography styles */}
          <article 
            className="prose prose-lg prose-slate dark:prose-invert max-w-none 
            prose-headings:font-display prose-headings:font-bold 
            prose-h2:text-3xl prose-h2:mt-16 prose-h2:mb-8
            prose-h3:text-2xl prose-h3:mt-12 prose-h3:mb-6
            prose-p:text-slate-600 dark:prose-p:text-slate-400 prose-p:leading-relaxed
            prose-strong:text-slate-900 dark:prose-strong:text-white
            prose-li:text-slate-600 dark:prose-li:text-slate-400"
            dangerouslySetInnerHTML={{ __html: selectedResource.content }}
          />

          <div className="mt-24 pt-12 border-t border-slate-100 dark:border-slate-800">
            <div className="bg-slate-50 dark:bg-slate-900 p-8 lg:p-12 rounded-[40px] flex flex-col md:flex-row items-center justify-between gap-8">
              <div className="max-w-md">
                <h4 className="text-2xl font-display font-bold text-slate-900 dark:text-white mb-2">Need direct advice on this?</h4>
                <p className="text-slate-500">Our senior partners can guide you through these 2026 legislative changes in a private session.</p>
              </div>
              <button 
                onClick={() => setPage('booking')}
                className="whitespace-nowrap px-8 py-4 bg-primary text-white font-bold rounded-2xl shadow-xl hover:scale-105 transition-all"
              >
                Book Session Now
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="pt-32 pb-24 bg-slate-50 dark:bg-slate-950 min-h-screen">
      <div className="max-w-7xl mx-auto px-4">
        {/* Hero Section */}
        <header className="text-center max-w-3xl mx-auto mb-16 animate-reveal">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-primary/5 dark:bg-white/5 text-primary dark:text-accent-400 text-[10px] font-black uppercase tracking-widest mb-6 border border-primary/10">
            <span className="material-symbols-rounded text-sm">auto_stories</span>
            Knowledge Base & Insights
          </div>
          <h1 className="text-5xl lg:text-6xl font-display font-bold text-slate-900 dark:text-white mb-6">
            Compliance <span className="text-primary italic">Intelligence.</span>
          </h1>
          <p className="text-xl text-slate-600 dark:text-slate-400 leading-relaxed">
            Stay ahead of the 2026 Nigerian regulatory shifts. From new tax bills to algorithmic accounting, we break down what founders need to know.
          </p>
        </header>

        {/* Filter Bar */}
        <div className="flex flex-wrap justify-center gap-3 mb-12 animate-reveal delay-100">
          {categories.map(cat => (
            <button
              key={cat}
              onClick={() => setFilter(cat)}
              className={`px-6 py-2.5 rounded-full text-sm font-bold transition-all border ${filter === cat ? 'bg-primary text-white border-primary shadow-lg shadow-primary/20' : 'bg-white dark:bg-slate-900 text-slate-500 border-slate-200 dark:border-slate-800 hover:border-primary/30'}`}
            >
              {cat}
            </button>
          ))}
        </div>

        {/* Resource Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-2 gap-8">
          {filteredResources.map((resource, i) => (
            <article 
              key={resource.id}
              onClick={() => setSelectedResource(resource)}
              className="group bg-white dark:bg-slate-900 rounded-[40px] border border-slate-100 dark:border-slate-800 p-8 lg:p-10 shadow-sm hover:shadow-2xl hover:border-primary/20 hover:-translate-y-1 transition-all duration-500 flex flex-col cursor-pointer animate-reveal"
              style={{ animationDelay: `${i * 100}ms` }}
            >
              <div className="flex justify-between items-start mb-8">
                <span className="px-4 py-1.5 rounded-full bg-slate-50 dark:bg-slate-800 text-slate-400 text-[10px] font-black uppercase tracking-widest border border-slate-100 dark:border-slate-700">
                  {resource.category}
                </span>
                {resource.trending && (
                  <div className="flex items-center gap-2 text-primary dark:text-accent-400">
                    <span className="material-symbols-rounded text-sm fill-1">trending_up</span>
                    <span className="text-[10px] font-black uppercase tracking-widest">Trending for 2026</span>
                  </div>
                )}
              </div>

              <h2 className="text-2xl lg:text-3xl font-display font-bold text-slate-900 dark:text-white mb-6 group-hover:text-primary transition-colors leading-tight">
                {resource.title}
              </h2>
              
              <p className="text-slate-600 dark:text-slate-400 mb-8 leading-relaxed flex-grow">
                {resource.excerpt}
              </p>

              <div className="pt-8 border-t border-slate-50 dark:border-slate-800 flex items-center justify-between">
                <div className="flex items-center gap-4 text-slate-400 text-xs font-bold">
                  <span className="flex items-center gap-1.5">
                    <span className="material-symbols-rounded text-base">calendar_today</span>
                    {resource.date}
                  </span>
                  <span className="w-1 h-1 bg-slate-200 rounded-full"></span>
                  <span className="flex items-center gap-1.5">
                    <span className="material-symbols-rounded text-base">schedule</span>
                    {resource.readTime}
                  </span>
                </div>
                <button className="text-primary dark:text-accent-400 font-black text-[10px] uppercase tracking-widest flex items-center gap-2 group-hover:gap-3 transition-all">
                  Read Full Analysis
                  <span className="material-symbols-rounded">arrow_right_alt</span>
                </button>
              </div>
            </article>
          ))}
        </div>

        {/* AI FAQ Section */}
        <section className="mt-32">
          <div className="bg-slate-900 rounded-[60px] p-12 lg:p-24 text-white relative overflow-hidden">
            <div className="absolute top-0 right-0 w-96 h-96 bg-primary/20 rounded-full blur-[120px] -translate-y-1/2 translate-x-1/2"></div>
            <div className="relative z-10 max-w-4xl mx-auto">
              <h3 className="text-xs font-black uppercase tracking-[0.4em] text-accent-400 mb-8">2026 Compliance Intelligence</h3>
              <div className="space-y-12">
                <div>
                  <h4 className="text-2xl font-display font-bold mb-4 text-white">What is the "Universal TIN" enforcement?</h4>
                  <p className="text-slate-400 text-lg leading-relaxed">
                    By 2026, the FIRS projects that a Tax Identification Number (TIN) will be required for all bank account operations, linked directly to your NIN. This creates a unified identity for fiscal monitoring.
                  </p>
                </div>
                <div className="w-full h-px bg-white/10"></div>
                <div>
                  <h4 className="text-2xl font-display font-bold mb-4 text-white">How does FinTrack help with the CAC de-registration purge?</h4>
                  <p className="text-slate-400 text-lg leading-relaxed">
                    We perform automated status audits and back-file outstanding annual returns and PSC/BOR records to ensure your company is not struck off the register in the scheduled 2026 exercise.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default Resources;
